import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardContent, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Building, 
  AlertTriangle, 
  Mail, 
  FileText, 
  DollarSign, 
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  ArrowRight,
  Globe,
  Search,
  Star,
  Rocket
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import proApiService from '../../services/proApiService';

const MainDashboard = () => {
  const { user, hasPermission } = useAuth();
  const [stats, setStats] = useState({
    projects: { total: 0, active: 0, completed: 0 },
    disputes: { total: 0, active: 0, resolved: 0 },
    communications: { sent: 0, pending: 0 },
    revenue: { total: 0, pending: 0 }
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load project statistics
      if (hasPermission('projects.read')) {
        const projectStats = await proApiService.getProjectDashboard();
        setStats(prev => ({ ...prev, projects: projectStats }));
      }

      // Load dispute statistics
      if (hasPermission('disputes.read')) {
        const disputeStats = await proApiService.getDisputesDashboard();
        setStats(prev => ({ ...prev, disputes: disputeStats }));
      }

      // Mock recent activity for now
      setRecentActivity([
        {
          id: 1,
          type: 'project',
          title: 'New project created: Johnson Residence Roof Repair',
          time: '2 hours ago',
          icon: Building
        },
        {
          id: 2,
          type: 'dispute',
          title: 'Insurance dispute filed for Smith Property',
          time: '4 hours ago',
          icon: AlertTriangle
        },
        {
          id: 3,
          type: 'communication',
          title: 'Proposal sent to ABC Insurance Co.',
          time: '6 hours ago',
          icon: Mail
        }
      ]);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {user?.first_name}!
        </h1>
        <p className="text-gray-600 mt-2">
          Here's what's happening with your home service jobs today.
        </p>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Projects Stats */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Projects</p>
                <p className="text-3xl font-bold text-blue-600">{stats.projects.active}</p>
                <p className="text-sm text-gray-500">
                  {stats.projects.total} total projects
                </p>
              </div>
              <Building className="w-12 h-12 text-blue-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        {/* Insurance Disputes */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Disputes</p>
                <p className="text-3xl font-bold text-red-600">{stats.disputes.active}</p>
                <p className="text-sm text-gray-500">
                  {stats.disputes.resolved} resolved
                </p>
              </div>
              <AlertTriangle className="w-12 h-12 text-red-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        {/* Communications */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Communications</p>
                <p className="text-3xl font-bold text-green-600">{stats.communications.sent}</p>
                <p className="text-sm text-gray-500">
                  {stats.communications.pending} pending
                </p>
              </div>
              <Mail className="w-12 h-12 text-green-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        {/* Revenue */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
                <p className="text-3xl font-bold text-purple-600">
                  {formatCurrency(stats.revenue.total)}
                </p>
                <p className="text-sm text-gray-500">
                  {formatCurrency(stats.revenue.pending)} pending
                </p>
              </div>
              <DollarSign className="w-12 h-12 text-purple-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {hasPermission('projects.create') && (
              <Link to="/dashboard/projects/new">
                <Button className="w-full justify-start bg-blue-600 hover:bg-blue-700">
                  <Building className="w-4 h-4 mr-2" />
                  Create New Project
                </Button>
              </Link>
            )}
            
            {hasPermission('disputes.create') && (
              <Link to="/dashboard/insurance/dispute/new">
                <Button variant="outline" className="w-full justify-start border-red-200 text-red-600 hover:bg-red-50">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  File Insurance Dispute
                </Button>
              </Link>
            )}

            {hasPermission('communications.create') && (
              <Link to="/dashboard/communications/new">
                <Button variant="outline" className="w-full justify-start">
                  <Mail className="w-4 h-4 mr-2" />
                  Send Communication
                </Button>
              </Link>
            )}

            {hasPermission('documents.create') && (
              <Link to="/dashboard/documents/new">
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="w-4 h-4 mr-2" />
                  Generate Document
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Activity</CardTitle>
            <Link to="/dashboard/activity">
              <Button variant="ghost" size="sm">
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.map((activity) => {
                const Icon = activity.icon;
                return (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <Icon className="w-4 h-4 text-gray-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{activity.title}</p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Marketing Services Promo Card */}
      <Card className="border-2 border-purple-300 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
        <CardContent className="p-6 relative">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Rocket className="w-6 h-6" />
                <span className="text-sm font-semibold bg-white/20 px-3 py-1 rounded-full">NEW ADD-ON SERVICE</span>
              </div>
              <h3 className="text-2xl font-bold mb-2">Boost Your Online Presence</h3>
              <p className="text-purple-100 mb-4 max-w-2xl">
                Get more leads with a professional website, local SEO, and Google Business optimization. 
                Let us handle your online marketing while you focus on your trade.
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  <span>Professional Website</span>
                </div>
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  <span>Local SEO</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  <span>5-Star Review System</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center lg:items-end gap-3">
              <div className="text-right">
                <p className="text-purple-200 text-sm">Starting at</p>
                <p className="text-3xl font-bold">$400<span className="text-lg font-normal">/mo</span></p>
                <p className="text-purple-200 text-sm">+ $1,000 setup</p>
              </div>
              <Link to="/dashboard/addons">
                <Button className="bg-white text-purple-700 hover:bg-purple-50 font-semibold px-6">
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Module Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Insurance Module Highlight */}
        <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-red-50">
          <CardContent className="p-6">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-8 h-8 text-orange-600 mr-3" />
              <div>
                <h3 className="font-semibold text-gray-900">Insurance Module</h3>
                <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">New</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Manage insurance disputes, communications with adjusters, and track claim progress.
            </p>
            <Link to="/dashboard/insurance">
              <Button variant="outline" size="sm" className="border-orange-300 text-orange-700 hover:bg-orange-100">
                Explore Module
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Project Management */}
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-6">
            <div className="flex items-center mb-4">
              <Building className="w-8 h-8 text-blue-600 mr-3" />
              <h3 className="font-semibold text-gray-900">Project Management</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Track projects from lead to completion with satellite estimates and worker assignments.
            </p>
            <Link to="/dashboard/projects">
              <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-100">
                View Projects
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Communications Hub */}
        <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-6">
            <div className="flex items-center mb-4">
              <Mail className="w-8 h-8 text-green-600 mr-3" />
              <h3 className="font-semibold text-gray-900">Communications</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Send proposals, status updates, and manage all stakeholder communications.
            </p>
            <Link to="/dashboard/communications">
              <Button variant="outline" size="sm" className="border-green-300 text-green-700 hover:bg-green-100">
                View Messages
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MainDashboard;